import { defineStore } from 'pinia';
import { addDoc, collection, doc, getDocs, orderBy, query, updateDoc } from 'firebase/firestore';
import { DEFAULT_ONBOARDING_TEMPLATES } from '~/lib/onboardingDefaults';
import { calculateOnboardingFrictionScore, getOnboardingFrictionFlags } from '~/lib/onboardingProgress';
import { stripUndefined } from '~/lib/stripUndefined';
import type { OnboardingRun, OnboardingTemplate } from '~/types/models';

function normalizeRun(run: OnboardingRun): OnboardingRun {
    return {
        ...run,
        stages: Array.isArray(run.stages) ? run.stages : [],
        requirements: Array.isArray(run.requirements) ? run.requirements : [],
        frictionFlags: Array.isArray(run.frictionFlags) ? run.frictionFlags : [],
        status: run.status || 'active',
    };
}

function normalizeTemplate(template: OnboardingTemplate): OnboardingTemplate {
    return {
        ...template,
        stages: Array.isArray(template.stages) ? template.stages : [],
        requirements: Array.isArray(template.requirements) ? template.requirements : [],
    };
}

export const useOnboardingStore = defineStore('onboarding', {
    state: () => ({
        templates: DEFAULT_ONBOARDING_TEMPLATES.map((template) => normalizeTemplate(template)),
        runs: [] as OnboardingRun[],
        loading: false,
        error: null as string | null,
    }),

    actions: {
        _getTenantId(): string {
            const tenantStore = useTenantStore();
            if (!tenantStore.currentTenantId) {
                throw new Error('No tenant selected. Please log in.');
            }
            return tenantStore.currentTenantId;
        },

        async fetchTemplates() {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();

            try {
                const snapshot = await getDocs(query(collection($db, `tenants/${tenantId}/onboardingTemplates`), orderBy('name', 'asc')));
                const customTemplates = snapshot.docs.map((entry) => normalizeTemplate({ id: entry.id, ...entry.data() } as OnboardingTemplate));
                const customIds = new Set(customTemplates.map((template) => template.id));
                this.templates = [
                    ...customTemplates,
                    ...DEFAULT_ONBOARDING_TEMPLATES
                        .filter((template) => !customIds.has(template.id))
                        .map((template) => normalizeTemplate(template)),
                ];
            } catch (error: any) {
                this.error = error?.message || 'Failed to load onboarding templates.';
                console.error('Error fetching onboarding templates:', error);
                this.templates = DEFAULT_ONBOARDING_TEMPLATES.map((template) => normalizeTemplate(template));
            }
        },

        async createTemplate(payload: Omit<OnboardingTemplate, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>) {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const now = new Date();

            const docRef = await addDoc(collection($db, `tenants/${tenantId}/onboardingTemplates`), stripUndefined({
                ...payload,
                tenantId,
                isDefault: false,
                isArchived: false,
                createdAt: now,
                updatedAt: now,
            }));

            await this.fetchTemplates();
            return docRef.id;
        },

        async updateTemplate(templateId: string, payload: Partial<OnboardingTemplate>) {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const existing = this.templates.find((template) => template.id === templateId);

            if (existing?.isDefault) {
                throw new Error('Default onboarding templates cannot be edited directly. Create a custom template from it instead.');
            }

            await updateDoc(doc($db, `tenants/${tenantId}/onboardingTemplates`, templateId), stripUndefined({
                ...payload,
                isDefault: false,
                updatedAt: new Date(),
            }));

            await this.fetchTemplates();
        },

        async fetchRuns() {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            this.loading = true;
            this.error = null;

            try {
                const snapshot = await getDocs(query(collection($db, `tenants/${tenantId}/onboardingRuns`), orderBy('updatedAt', 'desc')));
                this.runs = snapshot.docs.map((entry) => normalizeRun({ id: entry.id, ...entry.data() } as OnboardingRun));
            } catch (error: any) {
                this.error = error?.message || 'Failed to load onboarding runs.';
                console.error('Error fetching onboarding runs:', error);
            } finally {
                this.loading = false;
            }
        },

        async updateRun(runId: string, payload: Partial<OnboardingRun>) {
            const { $db } = useNuxtApp();
            const tenantId = this._getTenantId();
            const existing = this.runs.find((run) => run.id === runId);
            const nextRun = normalizeRun({
                ...(existing || {}),
                id: runId,
                ...payload,
                updatedAt: new Date(),
            } as OnboardingRun);
            const updatedData = stripUndefined({
                ...payload,
                frictionScore: calculateOnboardingFrictionScore(nextRun),
                frictionFlags: getOnboardingFrictionFlags(nextRun),
                updatedAt: new Date(),
            });

            await updateDoc(doc($db, `tenants/${tenantId}/onboardingRuns`, runId), updatedData);

            const index = this.runs.findIndex((run) => run.id === runId);
            if (index !== -1) {
                this.runs[index] = normalizeRun({ ...this.runs[index], ...updatedData } as OnboardingRun);
            }
        },

        async confirmPaymentRequirement(runId: string, requirementId: string) {
            const run = this.runs.find(item => item.id === runId);
            if (!run) throw new Error('Onboarding run not found.');
            const { $auth } = useNuxtApp();
            const token = await $auth.currentUser?.getIdToken();
            if (!token) throw new Error('You need to be signed in to confirm payment.');
            await $fetch('/api/onboarding/events', {
                method: 'POST',
                headers: { authorization: `Bearer ${token}` },
                body: {
                    tenantId: this._getTenantId(), clientId: run.clientId,
                    runId, requirementId, event: 'payment_confirmed',
                },
            });
            await this.fetchRuns();
        },

        async updateRunStage(runId: string, stageId?: string) {
            const { $auth } = useNuxtApp();
            const tenantId = this._getTenantId();
            const token = await $auth.currentUser?.getIdToken();

            if (!token) {
                throw new Error('You need to be signed in to update onboarding.');
            }

            const response = await $fetch<{ run: OnboardingRun }>(`/api/onboarding/runs/${runId}/advance`, {
                method: 'POST',
                body: {
                    tenantId,
                    ...(stageId ? { stageId } : {}),
                },
                headers: {
                    authorization: `Bearer ${token}`,
                },
            });

            const nextRun = normalizeRun(response.run);
            const index = this.runs.findIndex((run) => run.id === runId);
            if (index !== -1) {
                this.runs[index] = nextRun;
                return;
            }

            this.runs = [nextRun, ...this.runs];
        },
    },
});
